<template>
  <v-container style="padding-bottom:0px">
    <v-expansion-panels v-model="leadsPanelExpanded">
      <v-expansion-panel id="leads-panel">
        <v-expansion-panel-title>
          <div class="d-flex align-center" style="gap: 48px">
            <span>LEADS</span>
            <v-btn
              v-if="leadsPanelExpanded === 0"
              text
              color="#f5f5dc"
              size="small"
              @click.stop="addNewLead"
            >
              + Add
            </v-btn>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <!-- Filter Controls -->
          <div class="filter-controls">
            <v-select
              v-model="selectedRegion"
              :items="regionOptions"
              label="Region"
              class="filter-select"
              density="comfortable"
              hide-details
            />
            <v-select
              v-model="selectedSort"
              :items="sortOptions"
              label="Sort"
              class="filter-select"
              density="comfortable"
              hide-details
            />
          </div>

          <!-- Leads Records Box -->
          <div class="leads-box">
            <div v-for="lead in filteredAndSortedLeads" 
                 :key="lead._id"
                 class="lead-row"
                 :style="{ backgroundColor: lead._id % 2 === 0 ? '#f9f9f9' : '#ffffff' }"
            >
              <!-- Desktop Header -->
              <div v-if="!isMobile" class="lead-header-desktop" @click="toggleExpanded(lead._id)">
                <!-- Status Dot -->
                <div class="status-container-desktop" @click.stop="toggleStatusMenu(lead._id)">
                  <span 
                    class="status-dot-desktop"
                    :style="{ 
                      backgroundColor: lead.status ? getStatusColor(lead.status) : 'transparent',
                      border: lead.status ? 'none' : '1px dashed #ccc'
                    }"
                  ></span>
                  
                  <!-- Status Menu -->
                  <div v-if="showStatusMenu === lead._id" class="menu-dropdown-desktop status-menu-desktop">
                    <button @click.stop="updateStatus(lead._id, 'green')" class="status-button">
                      <span class="status-dot" style="background-color: #28a745"></span>
                      Active Customer
                    </button>
                    <button @click.stop="updateStatus(lead._id, 'yellow')" class="status-button">
                      <span class="status-dot" style="background-color: #ffc107"></span>
                      Pending
                    </button>
                    <button @click.stop="updateStatus(lead._id, 'red')" class="status-button">
                      <span class="status-dot" style="background-color: #dc3545"></span>
                      Not happening
                    </button>
                    <button @click.stop="updateStatus(lead._id, null)" class="status-button">
                      <span class="status-dot" style="border: 1px dashed #ccc"></span>
                      No Status
                    </button>
                  </div>
                </div>

                <!-- Desktop Lead Details -->
                <span class="lead-name-desktop">{{ lead.name || 'Unnamed Lead' }}</span>
                <span class="lead-region-desktop">{{ lead.city || 'No City' }}, {{ lead.region || 'No Region' }}</span>
                <span class="lead-phone-desktop">{{ prettyPhone(lead.phoneNumber) }}</span>

                <!-- Record Owner -->
                <div class="owner-container-desktop" @click.stop="toggleOwnerMenu(lead._id)">
                  <span class="record-owner-desktop">{{ lead.recordOwner || '. . .' }}</span>
                  
                  <!-- Owner Menu -->
                  <div v-if="showOwnerMenu === lead._id" class="menu-dropdown-desktop owner-menu-desktop">
                    <button 
                      v-for="owner in ownerOptions" 
                      :key="owner"
                      @click.stop="updateOwner(lead._id, owner)"
                    >
                      {{ owner }}
                    </button>
                  </div>
                </div>

                <!-- Last Updated -->
                <div class="flex-spacer-desktop">
                  <span class="last-updated-desktop" v-if="lead.lastUpdated">
                    {{ formatDate(lead.lastUpdated) }}
                  </span>
                </div>

                <div class="arrow-button-desktop">
                  {{ expandedLeadId === lead._id ? '▾' : '▸' }}
                </div>
              </div>

              <!-- Mobile Header -->
              <div v-else class="lead-header-mobile" @click="toggleExpanded(lead._id)">
                <!-- Row 1: Status and Name -->
                <div class="mobile-row-first">
                  <div class="status-container-mobile" @click.stop="toggleStatusMenu(lead._id)">
                    <span class="status-dot-mobile"
                      :style="{ 
                        backgroundColor: lead.status ? getStatusColor(lead.status) : 'transparent',
                        border: lead.status ? 'none' : '1px dashed #ccc'
                      }"
                    ></span>
                    
                    <!-- Status Menu Mobile -->
                    <div v-if="showStatusMenu === lead._id" class="menu-dropdown-mobile status-menu-mobile">
                      <button @click.stop="updateStatus(lead._id, 'green')" class="status-button">
                        <span class="status-dot" style="background-color: #28a745"></span>
                        Active Customer
                      </button>
                      <button @click.stop="updateStatus(lead._id, 'yellow')" class="status-button">
                        <span class="status-dot" style="background-color: #ffc107"></span>
                        Pending
                      </button>
                      <button @click.stop="updateStatus(lead._id, 'red')" class="status-button">
                        <span class="status-dot" style="background-color: #dc3545"></span>
                        Not happening
                      </button>
                      <button @click.stop="updateStatus(lead._id, null)" class="status-button">
                        <span class="status-dot" style="border: 1px dashed #ccc"></span>
                        No Status
                      </button>
                    </div>
                  </div>
                  <span class="lead-name-mobile">{{ lead.name || 'Unnamed Lead' }}</span>
                </div>

                <!-- Row 2: Record Owner and Arrow -->
                <div class="mobile-row-second">
                  <div class="owner-container-mobile" @click.stop="toggleOwnerMenu(lead._id)">
                    <span class="record-owner-mobile">{{ lead.recordOwner || '. . .' }}</span>
                    
                    <!-- Owner Menu Mobile -->
                    <div v-if="showOwnerMenu === lead._id" class="menu-dropdown-mobile owner-menu-mobile">
                      <button 
                        v-for="owner in ownerOptions" 
                        :key="owner"
                        @click.stop="updateOwner(lead._id, owner)"
                      >
                        {{ owner }}
                      </button>
                    </div>
                  </div>
                  <div class="arrow-button-mobile">
                    {{ expandedLeadId === lead._id ? '▾' : '▸' }}
                  </div>
                </div>

                <!-- Row 3: Region and Last Updated -->
                <div class="mobile-row-third">
                  <span class="lead-region-mobile">{{ lead.city || 'No City' }}, {{ lead.region || 'No Region' }}</span>
                  <span class="last-updated-mobile" v-if="lead.lastUpdated">
                    {{ formatDate(lead.lastUpdated) }}
                  </span>
                </div>
              </div>

              <!-- Expanded Content -->
              <div v-if="expandedLeadId === lead._id" class="expanded-content" @click.stop>
                <div class="notes-container" @click.stop :style="{ maxHeight: isEditingDetails ? '80px' : 'none', transition: 'max-height 0.3s ease-in-out' }">
                  <div class="notes-header">
                    <h3>Notes</h3>
                    <div class="notes-controls">
                      <button v-if="!editingNotes" class="icon-button" @click="startEdit(lead)">
                        ✎
                      </button>
                      <template v-else>
                        <v-icon 
                          class="ms-2" 
                          color="success" 
                          size="small"
                          @click="saveNotes(lead)"
                        >
                          mdi-check
                        </v-icon>
                        <v-icon 
                          class="ms-2" 
                          color="error" 
                          size="small"
                          @click="cancelEdit"
                        >
                          mdi-close
                        </v-icon>
                      </template>
                    </div>
                  </div>
                  <div class="notes-content">
                    <textarea
                      v-if="editingNotes && expandedLeadId === lead._id"
                      v-model="tempNotes"
                      class="notes-textarea"
                      placeholder="Enter notes..."
                      @mousedown.stop
                      @mouseup.stop
                    ></textarea>
                    <div v-else class="notes-text">{{ lead.notes || 'No notes yet' }}</div>
                  </div>
                </div>

                <!-- Edit Details Section -->
                <div class="edit-details-section" @click.stop>
                  <div class="edit-details-header" @click.stop="!isEditingDetails && toggleEditDetails(lead)">
                    <h3>Edit Details</h3>
                  </div>
                  
                  <div v-if="isEditingDetails && expandedLeadId === lead._id" class="edit-details-content" @click.stop>
                    <input
                      v-model="editForm.name"
                      @click.stop
                      @mousedown.stop
                      @mouseup.stop
                      type="text"
                      class="edit-input"
                      :placeholder="lead.name || 'company name'"
                    >
                    <input
                      v-model="editForm.city"
                      @click.stop
                      @mousedown.stop
                      @mouseup.stop
                      type="text"
                      class="edit-input"
                      :placeholder="lead.city || 'city'"
                    >
                    <select 
                      v-model="editForm.region" 
                      class="edit-input"
                      @click.stop
                      @mousedown.stop
                      @mouseup.stop
                    >
                      <option v-for="option in filteredRegionOptions" 
                              :key="option.value" 
                              :value="option.value">
                        {{ option.title }}
                      </option>
                    </select>
                    <input
                      v-model="editForm.phoneNumber"
                      type="text"
                      class="edit-input"
                      @click.stop
                      @mousedown.stop
                      @mouseup.stop
                      :placeholder="lead.phoneNumber || '+1 phone number eg \'12227773333\''"
                    >
                    <div class="note-edit-actions">
                      <button class="edit-action-button save" @click.stop="saveDetails(lead)">Save</button>
                      <button class="edit-action-button cancel" @click.stop="cancelEditDetails">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters } from 'vuex';
import { prettyPhone } from '../utils/helpers';

export default {
 name: 'LeadsPanel',

 data() {
   return {
     leads: [],
     leadsPanelExpanded: null,
     expandedLeadId: null,
     showStatusMenu: null,
     showOwnerMenu: null,
     editingNotes: false,
     isEditingDetails: false,
     tempNotes: '',
     editForm: {
       name: '',
       city: '',
       region: '',
       phoneNumber: ''
     },
     selectedRegion: 'WA',
     selectedSort: 'Last Updated',
     regionOptions: [
       { title: 'All Regions', value: 'All' },
       { title: 'Washington', value: 'WA' },       
       { title: 'Florida', value: 'FL' },
       { title: 'Texas', value: 'TX' },
       { title: 'Arizona', value: 'AZ' },
       { title: 'California', value: 'CA' },
       { title: 'Connecticut', value: 'CT' },
       { title: 'Idaho', value: 'ID' }
     ],
     sortOptions: [
       { title: 'Alphabetical', value: 'Alphabetical' },
       { title: 'Status', value: 'Status' },
       { title: 'Record Owner', value: 'Record Owner' },
       { title: 'Last Updated', value: 'Last Updated' }
     ],
     ownerOptions: ['Shaun Jones', 'Eric Juvet', 'Hunter Hudson', '. . .']
   };
 },

 computed: {
   ...mapState(['user']),
   ...mapGetters(['getSelectedUser']),

   isMobile() {
     return window.innerWidth <= 768;
   },

   filteredRegionOptions() {
     return this.regionOptions.filter(option => option.value !== 'All');
   },

   filteredAndSortedLeads() {
     let filtered = this.leads;
     if (this.selectedRegion !== 'All') {
       filtered = filtered.filter(lead => lead.region === this.selectedRegion);
     }

     return filtered.sort((a, b) => {
       switch (this.selectedSort) {
         case 'Alphabetical':
           return (a.name || '').localeCompare(b.name || '');
         
         case 'Status': {
           const statusOrder = { green: 1, yellow: 2, red: 3, null: 4 };
           return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
         }
         
         case 'Record Owner':
           if (!a.recordOwner && !b.recordOwner) return 0;
           if (!a.recordOwner) return 1;
           if (!b.recordOwner) return -1;
           return a.recordOwner.localeCompare(b.recordOwner);
         
         case 'Last Updated':
           if (!a.lastUpdated && !b.lastUpdated) return 0;
           if (!a.lastUpdated) return 1;
           if (!b.lastUpdated) return -1;
           return new Date(b.lastUpdated) - new Date(a.lastUpdated);

         default:
           return 0;
       }
     });
   }
 },

 methods: {

    async addNewLead() {
      try {
        const newLead = {
          name: 'New lead',
          city: 'New city',
          phoneNumber: '15550002222',
          region: this.selectedRegion === 'All' ? 'WA' : this.selectedRegion,
          recordOwner: '. . .'
        };
        
        const response = await axios.post('/portaldb/db/leads', newLead);
        if (!response.data.success) {
          throw new Error('Failed to create lead');
        }

        this.leads.unshift(response.data.record);
        this.selectedSort = 'Last Updated';
        
        this.expandedLeadId = response.data.record._id;
        this.toggleEditDetails(response.data.record);
        
        this.$nextTick(() => {
          document.querySelector('.leads-box').scrollTop = 0;
        });

      } catch (error) {
        console.error('Error creating lead:', error);
      }
    },

   prettyPhone,

   async fetchLeads() {
     try {
       const response = await axios.get('/portaldb/db/leads');
       this.leads = response.data;
     } catch (error) {
       console.error('Error fetching leads:', error);
     }
   },

   toggleExpanded(leadId) {
     this.expandedLeadId = this.expandedLeadId === leadId ? null : leadId;
     if (!this.expandedLeadId) {
       this.isEditingDetails = false;
     }
   },

   toggleStatusMenu(leadId) {
     console.log('Before toggle status menu: ', this.showStatusMenu);
     // Close owner menu if open
     this.showOwnerMenu = null;
     // Toggle status menu
     this.showStatusMenu = this.showStatusMenu === leadId ? null : leadId;
     console.log('After toggle status menu: ', this.showStatusMenu);
   },

   toggleOwnerMenu(leadId) {
     // Close status menu if open
     this.showStatusMenu = null;
     // Toggle owner menu
     this.showOwnerMenu = this.showOwnerMenu === leadId ? null : leadId;
   },

   getStatusColor(status) {
     switch(status) {
       case 'red': return '#dc3545';
       case 'yellow': return '#ffc107';
       case 'green': return '#28a745';
       default: return '#ffffff';
     }
   },

   formatDate(dateString) {
     const date = new Date(dateString);
     return date.toLocaleDateString('en-US', {
       weekday: 'short',
       month: 'short',
       day: 'numeric'
     });
   },

   formatTimestamp() {
     const date = new Date();
     const formattedDate = date.toLocaleDateString('en-US', {
       weekday: 'short',
       month: 'short',
       day: 'numeric'
     });
     const formattedTime = date.toLocaleTimeString('en-US', {
       hour: 'numeric',
       minute: '2-digit',
       hour12: true
     }).toLowerCase();
     
     return `${formattedDate} ${formattedTime}`;
   },

   async updateStatus(leadId, status) {
     try {
       const response = await axios.put(`/portaldb/db/updateLeadProperties/${leadId}`, {
         status
       });
       
       if (response.data.success) {
         // Update the lead in our local state
         const leadIndex = this.leads.findIndex(lead => lead._id === leadId);
         if (leadIndex !== -1) {
           this.leads[leadIndex] = {
             ...this.leads[leadIndex],
             status,
             lastUpdated: response.data.updatedFields.lastUpdated
           };
         }
       }
     } catch (error) {
       console.error('Error updating status:', error);
     }
     this.showStatusMenu = null;
   },

   async updateOwner(leadId, owner) {
     try {
       const updates = {
         recordOwner: owner,
         status: owner === ". . ." ? null : 'yellow'
       };

       const response = await axios.put(`/portaldb/db/updateLeadProperties/${leadId}`, updates);
       
       if (response.data.success) {
         // Update the lead in our local state
         const leadIndex = this.leads.findIndex(lead => lead._id === leadId);
         if (leadIndex !== -1) {
           this.leads[leadIndex] = {
             ...this.leads[leadIndex],
             ...updates,
             lastUpdated: response.data.updatedFields.lastUpdated
           };
         }
       }
     } catch (error) {
       console.error('Error updating owner:', error);
     }
     this.showOwnerMenu = null;
   },

   startEdit(lead) {
     this.editingNotes = true;
     const timestamp = this.formatTimestamp();
     const userName = this.getSelectedUser.name;
     const newNotePrefix = `${timestamp} - ${userName}\n\n\n`;
     this.tempNotes = newNotePrefix + (lead.notes || '');
   },

   async saveNotes(lead) {
     try {
       const response = await axios.put(`/portaldb/db/updateLeadProperties/${lead._id}`, {
         notes: this.tempNotes
       });
       
       if (response.data.success) {
         const leadIndex = this.leads.findIndex(l => l._id === lead._id);
         if (leadIndex !== -1) {
           this.leads[leadIndex] = {
             ...this.leads[leadIndex],
             notes: this.tempNotes,
             lastUpdated: response.data.updatedFields.lastUpdated
           };
         }
       }
       this.editingNotes = false;
       this.tempNotes = '';
     } catch (error) {
       console.error('Error saving notes:', error);
     }
   },

   cancelEdit() {
     this.editingNotes = false;
     this.tempNotes = '';
   },

   toggleEditDetails(lead) {
     this.isEditingDetails = !this.isEditingDetails;
     if (this.isEditingDetails) {
       this.editForm = {
         name: lead.name || '',
         city: lead.city || '',
         region: lead.region || '',
         phoneNumber: lead.phoneNumber || ''
       };
     }
   },

  async saveDetails(lead) {
    try {
      const response = await axios.put(`/portaldb/db/updateLeadProperties/${lead._id}`, {
        name: this.editForm.name,
        city: this.editForm.city,
        region: this.editForm.region,
        phoneNumber: this.editForm.phoneNumber
      });
      
      if (response.data.success) {
        const leadIndex = this.leads.findIndex(l => l._id === lead._id);
        if (leadIndex !== -1) {
          this.leads[leadIndex] = {
            ...this.leads[leadIndex],
            ...this.editForm,
            lastUpdated: response.data.updatedFields.lastUpdated
          };
        }
        this.isEditingDetails = false;
      }
    } catch (error) {
      console.error('Error saving details:', error);
    }
  },

  cancelEditDetails() {
    this.isEditingDetails = false;
    this.editForm = {
      name: '',
      city: '',
      region: '',
      phoneNumber: ''
    };
  },

   handleOutsideClick(event) {
     if (!event.target.closest('.status-container') && !event.target.closest('.owner-container')) {
       this.showStatusMenu = null;
       this.showOwnerMenu = null;
     }
   }
 },

 mounted() {
   this.fetchLeads();
   document.addEventListener('click', this.handleOutsideClick);
 },

 beforeUnmount() {
   document.removeEventListener('click', this.handleOutsideClick);
 }
};
</script>

<style scoped>
#leads-panel {
 margin-top: 20px;
}

.filter-controls {
 display: flex;
 gap: 20px;
 margin-bottom: 20px;
}

.filter-select {
 width: 200px;
}

.leads-box {
 border: 4px solid #d2b48c;
 padding: 20px;
 max-height: 600px;
 overflow-y: auto;
}

.lead-row {
 border: 1px solid #d2b48c;
 margin-bottom: 10px;
 border-radius: 4px;
 cursor: pointer;
}

.lead-header-desktop {
 background-color: #f5f5dc;
 padding: 0px;
 padding-left: 4px;
 display: flex;
 align-items: center;
 gap: 20px;
 min-height: 48px;
 position: relative;
}

.status-container-desktop {
 position: relative;
 cursor: pointer;
}

.status-dot-desktop {
 width: 16px;
 height: 16px;
 border-radius: 50%;
 display: inline-block;
 margin-left: 8px;
 margin-top: 8px;
}

.lead-name-desktop {
 width: 240px;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
}

.lead-region-desktop {
 width: 150px;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
}

.lead-phone-desktop {
 width: 120px;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
 text-decoration: none;
 color: inherit;
}

.record-owner-desktop {
 width: 150px;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
}

.flex-spacer-desktop {
 flex: 1;
 display: flex;
 justify-content: flex-end;
 padding-right: 8px;
}

.last-updated-desktop {
 color: #666;
 font-size: 0.9em;
}

.arrow-button-desktop {
 color: #8b4513;
 font-size: 36px;
 display: flex;
 align-items: center;
}

.menu-dropdown-desktop {
 position: absolute;
 top: 100%;
 left: 0;
 background-color: white;
 border: 1px solid #d2b48c;
 box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
 border-radius: 4px;
 z-index: 100;
 min-width: 150px;
}

.menu-dropdown-desktop button {
 display: block;
 width: 100%;
 text-align: left;
 padding: 8px 12px;
 border: none;
 background: none;
 cursor: pointer;
}

.menu-dropdown-desktop button:hover {
 background-color: #f5f5dc;
}

.expanded-content {
 padding: 20px;
 background-color: white;
 border-top: 1px solid #d2b48c;
}

.notes-container {
 height: 100%;
 display: flex;
 flex-direction: column;
}

.notes-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 10px;
 padding: 0 10px;
}

.notes-header h3 {
 margin: 0;
 color: #8b4513;
}

.notes-controls {
 display: flex;
 gap: 8px;
}

.icon-button {
 background: none;
 border: none;
 font-size: 16px;
 color: #8b4513;
 cursor: pointer;
 padding: 4px 8px;
 border-radius: 4px;
 transition: all 0.2s ease;
}

.icon-button:hover {
 background-color: rgba(139, 69, 19, 0.1);
}

.notes-content {
 flex-grow: 1;
 padding: 0 10px;
}

.notes-textarea {
 width: 100%;
 height: 100%;
 min-height: 200px;
 padding: 10px;
 border: 1px solid #d2b48c;
 border-radius: 4px;
 font-family: inherit;
 font-size: inherit;
 resize: none;
 line-height: 1.5;
}

.notes-textarea:focus {
 outline: none;
 border-color: #8b4513;
 box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
}

.notes-text {
 white-space: pre-wrap;
 line-height: 1.5;
 padding: 10px;
 border: 1px solid transparent;
 min-height: 200px;
}

.edit-details-section {
 border: 1px solid #d2b48c;
 border-radius: 0.5rem;
 padding: 1rem;
 margin-top: 1rem;
 overflow: hidden;
}

.edit-details-header {
 cursor: pointer;
}

.edit-details-header h3 {
 font-weight: 500;
 margin: 0;
 color: #8b4513;
}

.edit-details-content {
 display: flex;
 flex-direction: column;
 gap: 12px;
 margin-top: 12px;
 transition: height 0.3s ease-in-out;
}

.edit-input {
 padding: 8px;
 border: 1px solid #d2b48c;
 border-radius: 4px;
 font-family: inherit;
 font-size: inherit;
}

.edit-input:focus {
 outline: none;
 border-color: #8b4513;
 box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
 font-size: 1.25rem !important;
 font-weight: 600 !important;
 color: #ffffff !important;
 background-color: #d2b48c !important;
 border: 1px solid #d2b48c !important;
 min-height: 64px !important;
 padding: 16px !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
 color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
 background-color: #f5f5dc !important;
 border: 1px solid #d2b48c !important;
 border-top: none !important;
}

.status-button {
 display: flex !important;
 align-items: center;
 gap: 8px;
}

.status-dot {
 width: 12px;
 height: 12px;
 border-radius: 50%;
 display: inline-block;
}

.note-edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.edit-action-button {
  padding: 4px 12px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.edit-action-button.save {
  background-color: #d2b48c;
  color: white;
}

.edit-action-button.save:hover {
  background-color: #8b4513;
  border-color: #8b4513;
}

.edit-action-button.cancel {
  background-color: white;
  color: #666;
}

.edit-action-button.cancel:hover {
  background-color: #f5f5f5;
}

@media (max-width: 600px) {
 .expanded-content {
   padding: 4px;
 }

 :deep(.v-expansion-panel-text__wrapper) {
   padding-left: 6px;
   padding-right: 6px;
   padding-top: 6px;    
 }

 .leads-box {
   border: 0px solid #d2b48c;
   padding: 0px;
   max-height: 600px;
   overflow-y: auto;
 }

 .filter-controls {
   flex-direction: column;
   gap: 10px;
 }

 .filter-select {
   width: 100%;
 }

 .lead-header-mobile {
   background-color: #f5f5dc;
   padding: 8px;
   position: relative;
   display: flex;
   flex-direction: column;
   gap: 0;
   overflow: visible;
 }

 .status-container-mobile {
   position: relative;
   cursor: pointer;
   overflow: visible;
 }

 .status-dot-mobile {
   width: 20px;
   height: 20px;
   border-radius: 50%;
   display: inline-block;
   margin-top: 6px;
   margin-right: 10px;
   margin-left: -4px;
 }

 .lead-name-mobile {
   font-weight: bold;
   width: 200px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
 }

 .lead-region-mobile {
   width: 140px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   margin-left: 28px;
 }

 .record-owner-mobile {
   width: 150px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   margin-left: 28px;
 }

 .flex-spacer-mobile {
   flex: 1;
   display: flex;
   justify-content: flex-end;
   padding-right: 8px;
 }

 .last-updated-mobile {
   color: #666;
   font-size: 0.9em;
 }

 .arrow-button-mobile {
   color: #8b4513;
   font-size: 36px;
   display: flex;
   align-items: center;
 }

 .menu-dropdown-mobile {
   position: absolute;
   top: 100%;
   left: 0;
   width: 200px;
   background-color: white;
   border: 1px solid #d2b48c;
   box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
   border-radius: 4px;
   z-index: 100;
 }

 .status-menu-mobile {
   top: 35px;
   left: 25px;
 }

 .owner-menu-mobile {
   top: 50px;
   left: 20px;
 }

 .menu-dropdown-mobile button {
   display: block;
   width: 100%;
   text-align: left;
   padding: 8px 12px;
   border: none;
   background: none;
   cursor: pointer;
 }

 .menu-dropdown-mobile button:hover {
   background-color: #f5f5dc;
 }

 .mobile-row-first,
 .mobile-row-second,
 .mobile-row-third {
   display: flex;
   align-items: center;
   height: 22px;
   padding: 0 8px;
 }

 .mobile-row-first {
   justify-content: flex-start;
   overflow: visible;
   position: relative;
 }

 .mobile-row-second {
   justify-content: space-between;
 }

 .mobile-row-third {
   justify-content: space-between;
 }

 .owner-container-mobile {
   position: relative;
 }
}
</style>
